/**
 * Web Push for the installed app: registers the service worker, subscribes this browser with the server's
 * public VAPID key and sends the subscription to the backend. Nothing here is secret.
 */
import { api } from './api';

export interface PushConfig {
  configured: boolean;
  publicKey: string | null;
  mySubscriptions: number;
  endpoints: string[];
}

export type PushSupport = 'supported' | 'unsupported' | 'ios-needs-install';

/** iPhone/iPad allow Web Push only for a site added to the Home Screen and opened from its icon. */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true;
}

export function pushSupport(): PushSupport {
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
  if (isIos() && !isStandalone()) return 'ios-needs-install';
  return hasApis ? 'supported' : 'unsupported';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

/** This browser's current push subscription endpoint, if any. */
export async function currentEndpoint(): Promise<string | null> {
  const reg = await registerWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}

export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications were not allowed on this device.');
  const reg = await registerWorker();
  if (!reg) throw new Error('This browser cannot install the notification worker.');
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await api.post('/api/v1/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
}

export async function disablePush(): Promise<void> {
  const reg = await registerWorker();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api.post('/api/v1/push/unsubscribe', { endpoint: sub.endpoint });
  await sub.unsubscribe();
}
